"use client";
import { Button } from "@/components/ui/Button";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { Link } from "@/i18n/navigation";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect } from "react";

export default function Error({ error, reset }) {
  const isDevelopment = process.env.NODE_ENV === "development";
  const t = useTranslationDetector("error");

  useEffect(() => {
    console.error(error);
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-xl text-center">
        {/* Icon */}
        <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-red-100">
          <FontAwesomeIcon
            icon={byPrefixAndName.fas["triangle-exclamation"]}
            className="text-2xl text-red-600"
          />
        </div>

        {/* Title */}
        <h1 className="mb-4 text-2xl font-semibold text-gray-900">{t("title")}</h1>

        {/* Description */}
        <p className="mb-8 text-gray-600">
          {isDevelopment
            ? t("developmentDescription")
            : t("productionDescription")}
        </p>

        {/* Development Error Details */}
        {isDevelopment && (
          <div className="mb-8 rounded-lg border border-red-200 bg-red-50 p-4 text-left">
            <h3 className="mb-2 text-sm font-semibold text-red-800">
              {t("errorDetailsTitle")}
            </h3>
            <div className="font-mono text-xs break-all text-red-700">
              <strong>{t("errorMessage")}:</strong>{" "}
              {error?.message || t("unknown")}
            </div>
            {error?.stack && (
              <details className="mt-2">
                <summary className="cursor-pointer text-xs text-red-700 hover:text-red-800">
                  {t("showStackTrace")}
                </summary>
                <pre className="mt-2 max-h-40 overflow-auto text-xs whitespace-pre-wrap text-red-600">
                  {error.stack}
                </pre>
              </details>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3">
          <Button
            onClick={() => reset()}
            className="w-full bg-blue-600 text-white hover:bg-blue-700"
          >
            {t("tryAgain")}
          </Button>

          <Button
            onClick={() => (window.location.href = "/")}
            variant="outline"
            className="w-full"
          >
            {t("goToHomePage")}
          </Button>
        </div>

        {/* Additional Help */}
        {!isDevelopment && (
          <div className="mt-8 text-sm text-gray-500">
            <p>
              {t("contactSupportText")}{" "}
              <Link
                href="/contact"
                className="text-blue-600 underline hover:text-blue-800"
              >
                {t("contactSupport")}
              </Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
